/* Pezzi condivisi fra la lista dei bot, il dettaglio e i due flussi.
 *
 * Stanno insieme perché devono dire la stessa cosa nello stesso modo: la
 * distinzione fra denaro vero e simulato scritta in due posti diversi è la
 * distinzione che, prima o poi, in uno dei due posti manca.
 */

import { Distintivo, IconaAttenzione } from '../../componenti';
import { IconaStrategia } from '../../componenti';
import { TernaMetriche } from '../../componenti';
import { modalitaBot, periodoBot, statoBot, ternaBot, type StatoBot } from './modello';
import type { Tradebot } from '../../api';

/* ==========================================================================
 * Modalità: denaro vero o simulato. Sempre a schermo, mai dedotta.
 * ======================================================================== */

/**
 * Un bot con `useRealMoney` muove soldi veri; senza, è una prova.
 * L'ambra non dice "guadagno" né "perdita": dice "attenzione, qui si rischia".
 * Il distintivo DEX è separato perché è ortogonale — un bot su catena può
 * essere sia vero sia simulato, e schiacciarli in un'etichetta sola nascondeva
 * proprio la parte che conta.
 */
export function DistintiviModalita({ bot }: { bot: Tradebot }) {
  const m = modalitaBot(bot);
  return (
    <>
      <Distintivo tono={m.denaroReale ? 'cautela' : 'neutro'}>{m.etichettaCapitale}</Distintivo>
      {m.suDex && <Distintivo contorno>{m.etichettaSede}</Distintivo>}
    </>
  );
}

/* ==========================================================================
 * Stato operativo
 * ======================================================================== */

export function IconaConPallino({
  strategia,
  stato,
  dim = 26,
}: {
  strategia: string;
  stato: StatoBot;
  dim?: number;
}) {
  return (
    <span className="tb-pallino-guscio">
      <IconaStrategia nome={strategia} dim={dim} />
      <span
        className={`tb-pallino tb-pallino--${stato.tono}${stato.pulsa ? ' tb-pallino--pulsa' : ''}`}
        aria-hidden="true"
      />
    </span>
  );
}

/** Etichetta di stato: testo e colore dicono la stessa cosa, per chi non
 *  distingue i colori e per chi guarda al sole. */
export function EtichettaStato({ stato }: { stato: StatoBot }) {
  return (
    <span className={`tb-stato-etichetta sb-tono-${stato.tono}`}>
      <span aria-hidden="true">{stato.pulsa ? '◍ ' : '● '}</span>
      {stato.etichetta}
    </span>
  );
}

/* ==========================================================================
 * La terna del bot
 * ======================================================================== */

/**
 * Profitto, drawdown e operazioni di un bot, col periodo di misura.
 *
 * Il drawdown non è una colonna del backend: si ricostruisce dai saldi di
 * uscita delle operazioni. Quando non si può, il motivo va scritto — un "n/d"
 * senza causa si legge come un guasto.
 */
export function TernaBotVista({
  bot,
  densita = 'compatta',
}: {
  bot: Tradebot;
  densita?: 'compatta' | 'riquadri' | 'piena';
}) {
  const terna = ternaBot(bot);
  const p = periodoBot(bot);

  return (
    <div>
      <TernaMetriche
        profitto={terna.profitto}
        drawdown={terna.drawdown}
        operazioni={terna.operazioni}
        periodo={p.etichetta}
        durata={p.durata}
        densita={densita}
      />
      {terna.motivoDrawdownMancante && (
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
          Drawdown non calcolabile: {terna.motivoDrawdownMancante}.
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 * Riga di stato con la sua nota
 * ======================================================================== */

export function RigaStato({ bot, arrestoRichiestoA }: { bot: Tradebot; arrestoRichiestoA?: Date | null }) {
  const stato = statoBot(bot, arrestoRichiestoA);
  return (
    <div className="tb-stato-riga">
      <EtichettaStato stato={stato} />
      {stato.nota && <div className="tb-stato-nota">{stato.nota}</div>}
    </div>
  );
}

/* ==========================================================================
 * Icone dei due flussi
 * ======================================================================== */

export function IconaModifica({ dim = 14 }: { dim?: number }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

export function IconaClona({ dim = 14 }: { dim?: number }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a1 1 0 0 1 1-1h9" />
    </svg>
  );
}

/** Il motivo per cui una cosa non si può fare, accanto alla cosa stessa. */
export function Motivo({ testo, grave = false }: { testo: string; grave?: boolean }) {
  return (
    <span className={`tb-motivo${grave ? ' tb-motivo--grave' : ''}`}>
      <span className="tb-motivo__icona" aria-hidden="true">
        <IconaAttenzione dim={13} />
      </span>
      <span>{testo}</span>
    </span>
  );
}

/* ==========================================================================
 * Interruttore a due stati
 * ======================================================================== */

export function Interruttore({
  etichetta,
  acceso,
  onCambia,
}: {
  etichetta: string;
  acceso: boolean;
  onCambia: () => void;
}) {
  return (
    <button
      type="button"
      className="tb-interruttore"
      onClick={onCambia}
      role="switch"
      aria-checked={acceso}
    >
      <span>{etichetta}</span>
      <span className={`tb-binario${acceso ? ' tb-binario--acceso' : ''}`} aria-hidden="true">
        <span className="tb-binario__pomello" />
      </span>
    </button>
  );
}
